import { getSubWithoutPrefix, useAuthState } from '@/auth/auth'
import { useSpaceListViews } from '@/hooks/useSpaceListViews'
import { deepEqual, useNavigate, useSearch } from '@tanstack/react-router'
import {
  DEFAULT_SPACE_LIST_SETTINGS,
  SpaceListColumnSettings,
  TSpaceListViewSettings,
} from '@valuecase/common'
import { useNotifications } from '@valuecase/ui-components'
import { isEqual } from 'lodash'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const cleanViewSettings = (
  settings: Partial<SpaceListColumnSettings>,
): Partial<SpaceListColumnSettings> => {
  const cleanedSettings = { ...settings }

  if (cleanedSettings.filters) {
    const { page, ...cleanedFilters } = cleanedSettings.filters
    cleanedSettings.filters = cleanedFilters
  }

  return cleanedSettings
}

interface SpaceListViewContextType {
  selectedView: TSpaceListViewSettings | null
  setSelectedView: (viewId: string) => void
  views: TSpaceListViewSettings[]
  isLoading: boolean
  error: Error | null
  createView: (settings: Partial<SpaceListColumnSettings>) => Promise<TSpaceListViewSettings | void>
  duplicateView: (viewId: string) => Promise<TSpaceListViewSettings>
  updateView: (params: {
    viewId: string
    settings: Partial<SpaceListColumnSettings>
  }) => Promise<TSpaceListViewSettings>
  setDefaultView: (viewId: string) => Promise<TSpaceListViewSettings>
  deleteView: (viewId: string) => Promise<void>
  placeholderView: TSpaceListViewSettings
  localViewSettings: Record<string, SpaceListColumnSettings>
  updateLocalViewState: (viewId: string, settings: Partial<SpaceListColumnSettings>) => void
  saveSelectedViewSettings: () => Promise<void>
  resetSelectedViewSettings: () => void
  selectedViewHasUnsavedChanges: boolean
  handleCreateDuplicateFromPlaceholderView: () => Promise<TSpaceListViewSettings | null>
}

const SpaceListViewContext = createContext<SpaceListViewContextType | null>(null)

export const SpaceListViewProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    views: rawViews,
    isLoading,
    error,
    createView,
    duplicateView,
    updateView,
    setDefaultView,
    deleteView,
  } = useSpaceListViews()

  const views = useMemo(() => rawViews ?? [], [rawViews])
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/dashboard/spaces' })
  const [selectedView, setSelectedViewState] = useState<TSpaceListViewSettings | null>(null)
  const [localViewSettings, setLocalViewSettings] = useState<
    Record<string, SpaceListColumnSettings>
  >({})
  const { success } = useNotifications()

  const auth = useAuthState()

  const placeholderView = useMemo(() => {
    return {
      id: 'placeholder',
      tenantId: 'placeholder',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: 'placeholder',
      viewType: 'SPACE_LIST',
      settings: {
        ...DEFAULT_SPACE_LIST_SETTINGS,
        filters: {
          ...DEFAULT_SPACE_LIST_SETTINGS.filters,
          ownerIds: [getSubWithoutPrefix(auth)],
        },
      },
    } as TSpaceListViewSettings
  }, [auth])

  const setSelectedView = useCallback(
    (viewId: string) => {
      navigate({
        to: '/spaces',
        search: (existingParams) => ({
          ...existingParams,
          viewId,
        }),
      })
    },
    [navigate],
  )

  // Update selected view when URL viewId changes
  useEffect(() => {
    const viewId = searchParams.viewId
    const view = views.find((v) => v.id === viewId)

    if (!view) {
      // Remove viewId from URL if view no longer exists
      searchParams.viewId = undefined
    }

    if (viewId && view && views.length > 0) {
      initializeSelectedView(view)
    } else if (views.length > 0) {
      // If no viewId in URL, select default view
      const defaultView = views.find((view) => view.settings.isDefault) || views[views.length - 1]
      initializeSelectedView(defaultView)
    } else {
      initializeSelectedView(placeholderView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, searchParams.viewId])

  const initializeSelectedView = useCallback(
    (view: TSpaceListViewSettings) => {
      const existingTemporaryFilters = localViewSettings[view.id]?.filters

      setSelectedViewState(view)

      if (existingTemporaryFilters) {
        navigate({
          to: '/spaces',
          search: (prev) => ({
            ...existingTemporaryFilters,
            ...(view.id !== 'placeholder' && { viewId: view.id }),
            ...(prev.createSpaceFromTemplate && {
              createSpaceFromTemplate: prev.createSpaceFromTemplate,
            }),
          }),
          replace: true,
        })
      } else {
        navigate({
          to: '/spaces',
          search: (prev) => ({
            ...view.settings.filters,
            ...(view.id !== 'placeholder' && { viewId: view.id }),
            ...(prev.createSpaceFromTemplate && {
              createSpaceFromTemplate: prev.createSpaceFromTemplate,
            }),
          }),
          replace: true,
        })
      }
    },
    [localViewSettings, navigate],
  )

  // Update local settings when URL filters change
  useEffect(() => {
    if (!selectedView) {
      return
    }

    const { viewId, ...filters } = searchParams

    // If the viewId in URL doesn't match selected view, don't update local state
    if (viewId !== selectedView.id && selectedView.id !== 'placeholder') {
      return
    }

    // Only update filters if they're different from the current view's filters
    const currentFilters = selectedView.settings.filters
    delete currentFilters?.page
    const newFilters = filters
    delete newFilters.page

    if (!deepEqual(currentFilters, newFilters)) {
      setLocalViewSettings((prev) => {
        const currentSettings = prev[selectedView.id]
        if (!currentSettings) {
          return {
            ...prev,
            [selectedView.id]: {
              ...selectedView.settings,
              filters: newFilters,
            },
          }
        }

        return {
          ...prev,
          [selectedView.id]: {
            ...currentSettings,
            filters: newFilters,
          },
        }
      })
    }
  }, [searchParams, selectedView])

  const updateLocalViewState = useCallback(
    (viewId: string, settings: Partial<SpaceListColumnSettings>) => {
      const existingSettings = localViewSettings[viewId]

      setLocalViewSettings((prev) => ({
        ...prev,
        [viewId]: {
          ...existingSettings,
          ...settings,
        },
      }))
    },
    [localViewSettings],
  )

  const saveSelectedViewSettings = useCallback(async () => {
    if (!selectedView) {
      return
    }

    const localSettings = localViewSettings[selectedView.id]
    if (!localSettings) {
      return
    }

    if (selectedView.id === 'placeholder') {
      // Create a new view from placeholder settings
      const newView = await createView({
        ...cleanViewSettings(localSettings),
        name: 'My Spaces',
        emoji: '',
        isDefault: true,
      })
      setSelectedView(newView.id)
      return
    }

    // Only save settings for the specified view, preserving name and emoji
    const updatedView = await updateView({
      viewId: selectedView.id,
      settings: {
        ...cleanViewSettings(localSettings),
        name: selectedView.settings.name,
        emoji: selectedView.settings.emoji,
        isDefault: selectedView.settings.isDefault,
      },
    })

    // Update the selected view with the new settings if it's the current view
    setSelectedView(updatedView.id)

    // Update local settings to match the saved view
    setLocalViewSettings((prev) => ({
      ...prev,
      [selectedView.id]: updatedView.settings,
    }))
  }, [selectedView, localViewSettings, updateView, setSelectedView, createView])

  const resetSelectedViewSettings = useCallback(() => {
    if (!selectedView) {
      return
    }

    // Reset local settings to match the view's settings
    setLocalViewSettings((prev) => ({
      ...prev,
      [selectedView.id]: selectedView.settings,
    }))

    // Reset URL to match view's original filters
    navigate({
      to: '/spaces',
      search: () => ({
        ...selectedView.settings.filters,
        ...(selectedView.id !== 'placeholder' && { viewId: selectedView.id }),
      }),
      replace: true,
    })
    success('View settings reset')
  }, [navigate, selectedView, success])

  const selectedViewHasUnsavedChanges = useMemo(() => {
    if (!selectedView || !localViewSettings[selectedView.id]) {
      return false
    }

    const localSettings = localViewSettings[selectedView.id]
    if (!localSettings || typeof localSettings !== 'object') {
      return false
    }

    // Create comparison objects without meta fields
    const viewSettingsWithoutMeta = { ...selectedView.settings }
    const localSettingsWithoutMeta = { ...localSettings }

    // Remove meta fields from both objects
    delete viewSettingsWithoutMeta.name
    delete viewSettingsWithoutMeta.emoji
    delete viewSettingsWithoutMeta.isDefault
    delete localSettingsWithoutMeta.name
    delete localSettingsWithoutMeta.emoji
    delete localSettingsWithoutMeta.isDefault

    // Now create deep copies for comparison
    const viewSettingsCopy = JSON.parse(JSON.stringify(viewSettingsWithoutMeta))
    const localSettingsCopy = JSON.parse(JSON.stringify(localSettingsWithoutMeta))

    const hasChanges = !isEqual(viewSettingsCopy, localSettingsCopy)

    return hasChanges
  }, [selectedView, localViewSettings])

  const handleDeleteView = useCallback(
    async (viewId: string) => {
      const viewIndex = views.findIndex((view) => view.id === viewId)
      await deleteView(viewId)
      if (selectedView?.id === viewId) {
        // If deleting selected view, select previous view or default or last view
        if (viewIndex > 0) {
          setSelectedView(views[viewIndex - 1].id)
        } else {
          const defaultView =
            views.find((view) => view.settings.isDefault) || views[views.length - 1]
          if (!defaultView) {
            return
          }
          setSelectedView(defaultView.id)
        }
      }
      // Clean up local settings for deleted view
      setLocalViewSettings((prev) => {
        const { [viewId]: _, ...rest } = prev
        return rest
      })
    },
    [deleteView, selectedView, setSelectedView, views],
  )

  const handleCreateDuplicateFromPlaceholderView = useCallback(async () => {
    if (!selectedView?.settings) {
      return null
    }
    const newView = await createView(cleanViewSettings(selectedView?.settings))

    const newState = {
      ...localViewSettings,
      [newView.id]: localViewSettings['placeholder'],
    }

    setLocalViewSettings(newState)

    const secondView = await createView(cleanViewSettings(newView.settings))

    success('View duplicated')
    return secondView
  }, [createView, localViewSettings, selectedView?.settings, success])

  const handleCreateView = useCallback(
    async (settings: Partial<SpaceListColumnSettings>) => {
      if (!selectedView) {
        return
      }
      const existingUnsavedChanges = localViewSettings[selectedView.id]

      // We store the unsaved changes of the selected view in the new view being created
      const newView = await createView(
        cleanViewSettings({
          ...settings,
          ...(existingUnsavedChanges && {
            filters: existingUnsavedChanges.filters,
            order: existingUnsavedChanges.order,
            visible: existingUnsavedChanges.visible,
          }),
        }),
      )

      // Remove the unsaved changes from the local view settings
      setLocalViewSettings((prev) => {
        const { [selectedView.id]: _, ...rest } = prev
        return rest
      })

      return newView
    },
    [createView, localViewSettings, selectedView],
  )

  const handleDuplicateView = useCallback(
    async (viewId: string) => {
      const duplicatedView = await duplicateView(viewId)
      setSelectedView(duplicatedView.id)
      return duplicatedView
    },
    [duplicateView, setSelectedView],
  )

  const handleUpdateView = useCallback(
    async (params: { viewId: string; settings: Partial<SpaceListColumnSettings> }) => {
      const updatedView = await updateView({
        viewId: params.viewId,
        settings: cleanViewSettings(params.settings),
      })
      if (selectedView?.id === params.viewId) {
        setSelectedView(updatedView.id)
      }
      return updatedView
    },
    [updateView, selectedView, setSelectedView],
  )

  const handleSetDefaultView = useCallback(
    async (viewId: string) => {
      const updatedView = await setDefaultView(viewId)
      setSelectedView(updatedView.id)
      return updatedView
    },
    [setDefaultView, setSelectedView],
  )

  const value = useMemo(
    () => ({
      selectedView,
      placeholderView,
      setSelectedView,
      views,
      isLoading,
      error,
      createView: handleCreateView,
      duplicateView: handleDuplicateView,
      updateView: handleUpdateView,
      setDefaultView: handleSetDefaultView,
      deleteView: handleDeleteView,
      localViewSettings,
      updateLocalViewState,
      saveSelectedViewSettings,
      resetSelectedViewSettings,
      selectedViewHasUnsavedChanges,
      handleCreateDuplicateFromPlaceholderView,
    }),
    [
      selectedView,
      setSelectedView,
      views,
      isLoading,
      error,
      placeholderView,
      handleCreateView,
      handleDuplicateView,
      handleUpdateView,
      handleSetDefaultView,
      handleDeleteView,
      localViewSettings,
      updateLocalViewState,
      saveSelectedViewSettings,
      resetSelectedViewSettings,
      selectedViewHasUnsavedChanges,
      handleCreateDuplicateFromPlaceholderView,
    ],
  )

  return <SpaceListViewContext.Provider value={value}>{children}</SpaceListViewContext.Provider>
}

export const useSpaceListView = () => {
  const context = useContext(SpaceListViewContext)
  if (!context) {
    throw new Error('useSpaceListView must be used within a SpaceListViewProvider')
  }
  return context
}
